'use client'

import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useBatchGetUploadFileUrlsMutation } from '@/api/nodes/mutations/upload/batch-get-upload-file-urls-mutation'
import {
    useBatchConfirmNodeFileUploadMutation,
    useBatchInvalidateQueriesOnConfirmNodeFileUpload,
} from '@/api/nodes/mutations/upload/batch-confirm-node-file-upload-mutation'
import { DirectoryImplementationType } from '@/features/drive/entities/directory/directory-context'
import { DirectoryFileUploadDynamicIsland } from '@/features/drive/functionalities/upload/directory-file-upload-dynamic-island'
import { QueryKey } from '@tanstack/react-query'
import { ChangeEvent, Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { INodeConnections } from '@/api/nodes/queries/get-node-content-query'
import { SystemLabel } from 'shared'
import { reconcileConfirmedFileUploads } from '@/features/drive/functionalities/upload/utils/reconcile-confirmed-file-uploads'

import {
    createDirectoryFileUploadQueue,
    type IFileUploadItemState,
    type IFileUploadState,
} from '@/features/drive/functionalities/upload/utils/directory-file-upload-queue'

export type {
    IFileUploadItemState,
    IFileUploadState,
} from '@/features/drive/functionalities/upload/utils/directory-file-upload-queue'

export const FILE_ACCEPT_VIDEO = ['video/mp4', 'video/quicktime', 'video/webm', '.mp4', '.mov', '.webm'] as const

export const FILE_ACCEPT_AUDIO = [
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/ogg',

    '.mp3',
    '.m4a',
    '.wav',
    '.ogg',
] as const

export const FILE_ACCEPT_IMAGE = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/svg+xml',
    'image/heic',
    'image/heif',

    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.avif',
    '.svg',
    '.heic',
    '.heif',
] as const

export const FILE_ACCEPT_FONT = [
    'font/ttf',
    'font/otf',
    'font/woff',
    'font/woff2',

    '.ttf',
    '.otf',
    '.woff',
    '.woff2',
] as const

export const FILE_ACCEPT_DATA = [
    'application/json',
    'application/xml',
    'text/csv',
    'text/markdown',
    '.json',
    '.xml',
    '.csv',
    '.md',
] as const

export const FILE_ACCEPT_DOCUMENT = ['application/pdf', '.pdf'] as const

export const FILE_ACCEPT_OTHER = ['application/zip', '.zip', '.safetensors'] as const

export const FILE_ACCEPT = [
    ...FILE_ACCEPT_VIDEO,
    ...FILE_ACCEPT_AUDIO,
    ...FILE_ACCEPT_IMAGE,
    ...FILE_ACCEPT_FONT,
    ...FILE_ACCEPT_DATA,
    ...FILE_ACCEPT_DOCUMENT,
    ...FILE_ACCEPT_OTHER,
] as const

const FILE_ACCEPT_STRING = FILE_ACCEPT.join(',')

/** Checks a browser File against a native accept string without silently dropping mismatches. */
export function fileMatchesAccept(file: File, accept: string | undefined) {
    if (!accept) return true
    const tokens = accept
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean)
    const type = (file.type || '').toLowerCase()
    const name = file.name.toLowerCase()
    return tokens.some((token) => {
        if (token.startsWith('.')) return name.endsWith(token)
        if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1))
        return type === token
    })
}

export interface IUseDirectoryFileUploadParams {
    path: string
    nodeContentQueryKey: QueryKey
    accept?: string
    productUuid?: string
    implementation: DirectoryImplementationType
    multiple?: boolean
    onUpload?: (files: INode[]) => void
    setIsLoading?: (isLoading: boolean) => void
    getPayload?: (file: File) => INodeConnections & {
        autoCategorize?: boolean
        systemLabels?: SystemLabel[]
        productUuid?: string | undefined
    }
    onUploadStateChange?: (state: IFileUploadState) => void
}

export const useDirectoryFileUpload = ({
    path,
    nodeContentQueryKey,
    accept = FILE_ACCEPT_STRING,
    onUpload,
    setIsLoading,
    productUuid,
    implementation,
    getPayload,
    onUploadStateChange,
    multiple = true,
}: IUseDirectoryFileUploadParams) => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [fileUploadState, setFileUploadState] = useState<IFileUploadState>({ state: 'idle', id: 0 })
    const [uploadQueue] = useState(() =>
        createDirectoryFileUploadQueue((state) => flushSync(() => setFileUploadState(state)))
    )
    const onUploadRef = useRef(onUpload)
    onUploadRef.current = onUpload

    useEffect(() => {
        setIsLoading?.(fileUploadState.state === 'uploading')
        onUploadStateChange?.(fileUploadState)
    }, [fileUploadState, onUploadStateChange, setIsLoading])

    useEffect(() => {
        if (fileUploadState.state !== 'done' || fileUploadState.failed > 0) return

        const timeout = setTimeout(() => {
            uploadQueue.resetCompleted()
        }, 2500)

        return () => clearTimeout(timeout)
    }, [fileUploadState, uploadQueue])

    const { mutateAsync: batchGetUploadUrlsAsync } = useBatchGetUploadFileUrlsMutation()
    const { mutateAsync: batchConfirmFileUploadAsync } = useBatchConfirmNodeFileUploadMutation(implementation, {
        infiniteNodeContentQueryKey: nodeContentQueryKey,
        invalidateQueries: false,
    })

    const invalidateQueries = useBatchInvalidateQueriesOnConfirmNodeFileUpload()

    const uploadFiles = useCallback(
        async (files: File[]) => {
            const BATCH_SIZE = 10
            const UPLOAD_CONCURRENCY = 5
            if (files.length === 0) return

            const incoming = files.map((file) => ({
                name: file.name,
                retryKey: `${file.name}:${file.size}:${file.lastModified}:${file.type}`,
                status: fileMatchesAccept(file, accept) ? ('queued' as const) : ('failed' as const),
                error: fileMatchesAccept(file, accept) ? undefined : 'Unsupported file type',
            }))
            return uploadQueue.enqueue(incoming, async (items, updateItem) => {
                const uploadedNodes: INode[] = []
                const acceptedFiles = files
                    .map((file, index) => ({ file, item: items[index] }))
                    .filter(({ item }) => item.status !== 'failed')
                const batches = acceptedFiles.reduce(
                    (result, entry, index) => {
                        if (index % BATCH_SIZE === 0) result.push([])
                        result[result.length - 1].push(entry)
                        return result
                    },
                    [] as { file: File; item: IFileUploadItemState }[][]
                )

                for (const batch of batches) {
                    let urlResults: Awaited<ReturnType<typeof batchGetUploadUrlsAsync>>
                    try {
                        urlResults = await batchGetUploadUrlsAsync({ fileNames: batch.map(({ file }) => file.name) })
                    } catch (error) {
                        const message = error instanceof Error ? error.message : 'Could not prepare upload'
                        for (const { item } of batch) updateItem(item.id, { status: 'failed', error: message })
                        continue
                    }

                    const succeeded: { file: File; item: IFileUploadItemState; s3Path: string }[] = []
                    let nextIndex = 0
                    const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, batch.length) }, async () => {
                        while (nextIndex < batch.length) {
                            const index = nextIndex++
                            const { file, item } = batch[index]
                            try {
                                updateItem(item.id, { status: 'uploading', error: undefined })
                                const response = await fetch(urlResults[index].presignedUrl, {
                                    method: 'PUT',
                                    body: file,
                                    headers: { 'Content-Type': file.type },
                                })
                                if (!response.ok) throw new Error(`Upload failed (${response.status})`)
                                updateItem(item.id, { status: 'confirming' })
                                succeeded.push({ file, item, s3Path: urlResults[index].path })
                            } catch (error) {
                                updateItem(item.id, {
                                    status: 'failed',
                                    error: error instanceof Error ? error.message : 'Upload failed',
                                })
                            }
                        }
                    })
                    await Promise.all(workers)

                    if (succeeded.length > 0) {
                        try {
                            const nodes = await batchConfirmFileUploadAsync({
                                files: succeeded.map(({ file, s3Path }) => ({
                                    name: file.name,
                                    filePath: s3Path,
                                    parentNodePath: path,
                                    productUuid,
                                    ...getPayload?.(file),
                                })),
                            })
                            const confirmed = reconcileConfirmedFileUploads(succeeded, nodes ?? [])
                            uploadedNodes.push(...confirmed.confirmedNodes)
                            for (const { id, ...outcome } of confirmed.outcomes) updateItem(id, outcome)
                        } catch (error) {
                            const message = error instanceof Error ? error.message : 'Could not confirm upload'
                            for (const { item } of succeeded) updateItem(item.id, { status: 'failed', error: message })
                        }
                    }
                    await invalidateQueries()
                }

                if (uploadedNodes.length > 0) onUploadRef.current?.(uploadedNodes)
                return uploadedNodes
            })
        },
        [
            accept,
            batchGetUploadUrlsAsync,
            batchConfirmFileUploadAsync,
            getPayload,
            path,
            productUuid,
            invalidateQueries,
            uploadQueue,
        ]
    )

    const onFileUpload = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const files = e.target?.files

            if (!files?.length) return

            // uploadFiles now invokes onUpload internally — do not double-fire.
            const selectedFiles = Array.from(files)
            // Permit selecting the same file again after an upload failure.
            e.target.value = ''
            await uploadFiles(selectedFiles)
        },
        [uploadFiles]
    )

    return {
        uploadFiles,
        fileUploadState,

        fileUploadNodes: (
            <Fragment>
                <DirectoryFileUploadDynamicIsland />

                <input
                    id="file"
                    type="file"
                    multiple={multiple}
                    ref={fileInputRef}
                    className="hidden"
                    onChange={onFileUpload}
                    {...(accept && { accept })}
                />
            </Fragment>
        ),

        openFileUpload: () => fileInputRef.current?.click(),
    }
}
